import Phaser from 'phaser';
import { GAME_CONFIG } from '../config/game';

export class PreloadScene extends Phaser.Scene {
  constructor() {
    super('PreloadScene');
  }

  preload() {
    const { width, height } = GAME_CONFIG;
    
    // Loading bar
    const progressBar = this.add.graphics();
    const progressBox = this.add.graphics();
    progressBox.fillStyle(0x222244, 0.8);
    progressBox.fillRect(width / 2 - 160, height / 2 - 25, 320, 50);

    const loadingText = this.add.text(width / 2, height / 2 - 50, 'Loading...', {
      fontFamily: 'Georgia, serif',
      fontSize: '24px',
      color: '#ffffff',
    });
    loadingText.setOrigin(0.5, 0.5);

    this.load.on('progress', (value: number) => {
      progressBar.clear();
      progressBar.fillStyle(0xffd700, 1);
      progressBar.fillRect(width / 2 - 150, height / 2 - 15, 300 * value, 30);
    });

    this.load.on('complete', () => {
      progressBar.destroy();
      progressBox.destroy();
      loadingText.destroy();
    });

    // Load actual generated assets
    this.loadGeneratedAssets();
    
    // Create placeholder graphics for things we don't have yet
    this.createPlaceholderAssets();
  }

  loadGeneratedAssets() {
    // ===== TOP-DOWN SPRITES (3/4 view for house exploration) =====
    this.load.image('david_sprite', 'assets/sprites/david_32x48.png');
    this.load.image('maya_sprite', 'assets/sprites/maya_32x48.png');
    this.load.image('enny_sprite', 'assets/sprites/enny_32x48.png');
    this.load.image('adam_sprite', 'assets/sprites/adam_32x48.png');
    this.load.image('eli_sprite', 'assets/sprites/eli_32x48.png');
    this.load.image('daniel_sprite', 'assets/sprites/daniel_32x48.png');
    this.load.image('emily_sprite', 'assets/sprites/emily_32x48.png');

    // ===== SIDE-VIEW SPRITES (for platformer/boss scenes) =====
    this.load.image('david_side_sprite', 'assets/sprites/side/david_side_32x48.png');
    this.load.image('maya_side_sprite', 'assets/sprites/side/maya_side_32x48.png');
    this.load.image('adam_side_sprite', 'assets/sprites/side/adam_side_32x48.png');
    this.load.image('emily_side_sprite', 'assets/sprites/side/emily_side_32x48.png');
    this.load.image('enny_side_sprite', 'assets/sprites/side/enny_side_32x48.png');

    // ===== SIDE-VIEW BACKGROUNDS =====
    this.load.image('emily_bedroom_side', 'assets/backgrounds/emily_bedroom_side.png');

    // ===== LEVEL BACKGROUNDS =====
    // World 1 - Morning Routine
    this.load.image('1-1_blanket_fortress', 'assets/backgrounds/1-1_blanket_fortress.png');
    this.load.image('1-2_clothes_chaos', 'assets/backgrounds/1-2_clothes_chaos.png');
    this.load.image('1-3_missing_shoe', 'assets/backgrounds/1-3_missing_shoe.png');
    this.load.image('1-B_morning_titan', 'assets/backgrounds/1-B_morning_titan.png');
    // World 2 - School & Homework
    this.load.image('2-1_kindergarten', 'assets/backgrounds/2-1_kindergarten.png');
    this.load.image('2-2_homework_hydra', 'assets/backgrounds/2-2_homework_hydra.png');
    this.load.image('2-3_friendship_trials', 'assets/backgrounds/2-3_friendship_trials.png');
    this.load.image('2-B_institution', 'assets/backgrounds/2-B_institution.png');
    // World 3 - Mealtime
    this.load.image('3-1_vegetable_vendetta', 'assets/backgrounds/3-1_vegetable_vendetta.png');
    this.load.image('3-2_snack_siege', 'assets/backgrounds/3-2_snack_siege.png');
    this.load.image('3-3_restaurant', 'assets/backgrounds/3-3_restaurant.png');
    this.load.image('3-B_dinner_titan', 'assets/backgrounds/3-B_dinner_titan.png');
    // World 4 - Bath & Hygiene
    this.load.image('4-1_soapy_showdown', 'assets/backgrounds/4-1_soapy_showdown.png');
    this.load.image('4-2_shampoo_standoff', 'assets/backgrounds/4-2_shampoo_standoff.png');
    this.load.image('4-3_teeth_war', 'assets/backgrounds/4-3_teeth_war.png');
    this.load.image('4-B_cleanliness', 'assets/backgrounds/4-B_cleanliness.png');
    // World 5 - Bedtime
    this.load.image('5-1_one_more_story', 'assets/backgrounds/5-1_one_more_story.png');
    this.load.image('5-2_water_bathroom', 'assets/backgrounds/5-2_water_bathroom.png');
    this.load.image('5-3_midnight_visitor', 'assets/backgrounds/5-3_midnight_visitor.png');
    this.load.image('5-B_sandman', 'assets/backgrounds/5-B_sandman.png');
    // World 6 - Finale
    this.load.image('6-F_family_dinner', 'assets/backgrounds/6-F_family_dinner.png');

    // ===== FRICTION MAPS (Sobel edge detection for physics) =====
    // Level 1-1 Blanket Fortress
    this.load.image('friction_1-1', 'assets/friction/1-1_blanket_fortress_friction.png');
    this.load.json('edges_1-1', 'assets/friction/1-1_blanket_fortress_edges.json');
    // Level 1-2 Clothes Chaos
    this.load.image('friction_1-2', 'assets/friction/1-2_clothes_chaos_friction.png');
    this.load.json('edges_1-2', 'assets/friction/1-2_clothes_chaos_edges.json');
    // Level 1-3 Missing Shoe
    this.load.image('friction_1-3', 'assets/friction/1-3_missing_shoe_friction.png');
    this.load.json('edges_1-3', 'assets/friction/1-3_missing_shoe_edges.json');
    // Level 1-B Morning Titan
    this.load.image('friction_1-B', 'assets/friction/1-B_morning_titan_friction.png');
    this.load.json('edges_1-B', 'assets/friction/1-B_morning_titan_edges.json');

    // ===== FURNITURE (3/4 view) =====
    this.load.image('bunk_bed', 'assets/furniture/bunk_bed_96x128.png');
    this.load.image('fireplace', 'assets/furniture/fireplace_64x96.png');
    this.load.image('gaming_pc', 'assets/furniture/gaming_pc_station_64x64.png');
    this.load.image('couch', 'assets/furniture/living_couch_3seat_96x64.png');
    this.load.image('pink_cabinet', 'assets/furniture/pink_drawer_cabinet_48x80.png');
    this.load.image('pink_imac', 'assets/furniture/pink_imac_64x64.png');
    this.load.image('dining_table', 'assets/furniture/round_dining_table_96x96.png');

    // ===== TILES (top-down floor tiles) =====
    this.load.image('wood_floor', 'assets/tiles/test_wood_floor_64x64.png');
    this.load.image('green_carpet', 'assets/tiles/green_carpet_64x64.png');
  }

  createPlaceholderAssets() {
    // Player placeholder (32x48)
    const playerGraphics = this.make.graphics({ x: 0, y: 0 });
    playerGraphics.fillStyle(0x1a3a5c, 1);
    playerGraphics.fillRect(0, 0, 32, 48);
    playerGraphics.fillStyle(0xffd9b3, 1);
    playerGraphics.fillCircle(16, 12, 10); // Head
    playerGraphics.generateTexture('player', 32, 48);
    playerGraphics.destroy();

    // Emily/Boss placeholder (64x64)
    const bossGraphics = this.make.graphics({ x: 0, y: 0 });
    bossGraphics.fillStyle(0xc026d3, 1);
    bossGraphics.fillCircle(32, 32, 28);
    bossGraphics.fillStyle(0xffd9b3, 1);
    bossGraphics.fillCircle(32, 24, 12); // Head
    bossGraphics.generateTexture('emily_boss', 64, 64);
    bossGraphics.destroy();

    // Blanket/Duvet placeholder (128x96)
    const blanketGraphics = this.make.graphics({ x: 0, y: 0 });
    blanketGraphics.fillStyle(0xe879f9, 1);
    blanketGraphics.fillRoundedRect(0, 0, 128, 96, 16);
    blanketGraphics.fillStyle(0xf0abfc, 0.5);
    blanketGraphics.fillRoundedRect(10, 10, 108, 76, 12);
    blanketGraphics.generateTexture('blanket', 128, 96);
    blanketGraphics.destroy();

    // Pillow projectile (24x20)
    const pillowGraphics = this.make.graphics({ x: 0, y: 0 });
    pillowGraphics.fillStyle(0xffffff, 1);
    pillowGraphics.fillRoundedRect(0, 0, 24, 20, 6);
    pillowGraphics.generateTexture('pillow', 24, 20);
    pillowGraphics.destroy();

    // Pink pillow (parryable)
    const pinkPillowGraphics = this.make.graphics({ x: 0, y: 0 });
    pinkPillowGraphics.fillStyle(0xff69b4, 1);
    pinkPillowGraphics.fillRoundedRect(0, 0, 24, 20, 6);
    pinkPillowGraphics.generateTexture('pillow_pink', 24, 20);
    pinkPillowGraphics.destroy();

    // Stuffed animal (32x32)
    const stuffedGraphics = this.make.graphics({ x: 0, y: 0 });
    stuffedGraphics.fillStyle(0x8b4513, 1);
    stuffedGraphics.fillCircle(16, 16, 14);
    stuffedGraphics.fillStyle(0x000000, 1);
    stuffedGraphics.fillCircle(10, 12, 3);
    stuffedGraphics.fillCircle(22, 12, 3);
    stuffedGraphics.generateTexture('stuffed_animal', 32, 32);
    stuffedGraphics.destroy();

    // Heart (for HP)
    const heartGraphics = this.make.graphics({ x: 0, y: 0 });
    heartGraphics.fillStyle(0xff0000, 1);
    heartGraphics.fillCircle(8, 8, 6);
    heartGraphics.fillCircle(18, 8, 6);
    heartGraphics.fillTriangle(2, 10, 24, 10, 13, 24);
    heartGraphics.generateTexture('heart', 26, 26);
    heartGraphics.destroy();

    // Empty heart
    const emptyHeartGraphics = this.make.graphics({ x: 0, y: 0 });
    emptyHeartGraphics.lineStyle(2, 0x666666, 1);
    emptyHeartGraphics.strokeCircle(8, 8, 6);
    emptyHeartGraphics.strokeCircle(18, 8, 6);
    emptyHeartGraphics.lineBetween(2, 10, 13, 24);
    emptyHeartGraphics.lineBetween(24, 10, 13, 24);
    emptyHeartGraphics.generateTexture('heart_empty', 26, 26);
    emptyHeartGraphics.destroy();

    // Platform
    const platformGraphics = this.make.graphics({ x: 0, y: 0 });
    platformGraphics.fillStyle(0x4a5568, 1);
    platformGraphics.fillRect(0, 0, 128, 24);
    platformGraphics.fillStyle(0x718096, 1);
    platformGraphics.fillRect(0, 0, 128, 6);
    platformGraphics.generateTexture('platform', 128, 24);
    platformGraphics.destroy();

    // Bed platform (for boss level)
    const bedGraphics = this.make.graphics({ x: 0, y: 0 });
    bedGraphics.fillStyle(0x8b5a2b, 1);
    bedGraphics.fillRect(0, 0, 200, 32);
    bedGraphics.fillStyle(0xdeb887, 1);
    bedGraphics.fillRect(0, 0, 200, 8);
    bedGraphics.generateTexture('bed', 200, 32);
    bedGraphics.destroy();

    // Button
    const buttonGraphics = this.make.graphics({ x: 0, y: 0 });
    buttonGraphics.fillStyle(0xffd700, 1);
    buttonGraphics.fillRoundedRect(0, 0, 180, 50, 12);
    buttonGraphics.fillStyle(0xffea00, 1);
    buttonGraphics.fillRoundedRect(4, 4, 172, 42, 10);
    buttonGraphics.generateTexture('button', 180, 50);
    buttonGraphics.destroy();
  }

  create() {
    this.scene.start('TitleScene');
  }
}

